import { z } from 'zod';
import { checkReportRange, reportRangeShape, withRangeDefaults } from './report.schema.js';
/**
 * BO'LINMA STATISTIKASI — tuzilma bo'ylab pastga tushadigan kesim.
 *
 * Rahbarning savoli bitta va u har darajada bir xil: «qancha mol kirdi,
 * qanchasi hisobdan chiqdi». Javob esa tuzilmaning qaysi qatlamida
 * turganiga qarab boshqa-boshqa:
 *
 *   MTU → korxona → bo'linma → ichki ombor
 *
 * Shuning uchun shakl HAR DARAJADA bir xil: bitta jadval, bitta
 * so'rov, bitta komponent. Daraja bo'yicha to'rtta alohida shartnoma
 * yozilsa, nafas chizig'i va yig'ma raqamlar to'rt joyda takrorlanardi.
 */
export const FLOW_LEVELS = ['MTU', 'ENTERPRISE', 'SUBDIVISION', 'STOREROOM'];
export const flowLevelSchema = z.enum(FLOW_LEVELS);
/**
 * So'rov. `level` va `parentId` ikkovi ham MAJBURIY EMAS.
 *
 * Berilmasa, server doiradan kelib chiqib boshlang'ich darajani o'zi
 * tanlaydi: superadmin MTU larni, MTU rahbari o'z korxonalarini,
 * korxona boshlig'i o'z bo'linmalarini ko'radi. Shu tufayli front
 * «men kimman» degan savolni hal qilmaydi — u faqat ko'rsatadi.
 */
/**
 * Saralash kaliti.
 *
 * SERVERDA amalga oshadi, mijozda emas: bitta MTU da mingdan ortiq
 * korxona bo'lishi mumkin va ochiq sahifa ichida saralash «eng faol»
 * o'rniga «shu yigirmatasining eng faoli» ni ko'rsatardi — ya'ni
 * yolg'on reyting.
 */
export const FLOW_SORTS = ['name', 'in', 'out'];
export const flowSortSchema = z.enum(FLOW_SORTS);
/** Bir sahifadagi satrlar — ko'z bir marta yugurib chiqadigan miqdor. */
export const FLOW_PAGE_SIZE = 20;
export const flowQuerySchema = z
    .object({
    ...reportRangeShape,
    level: flowLevelSchema.optional(),
    /* Faqat raqam: identifikatorlar `bigint` va ular satr bo'lib yuradi. */
    parentId: z.string().regex(/^\d+$/, 'Noto\'g\'ri identifikator').optional(),
    /** Nom yoki kod bo'yicha qidiruv. */
    q: z.string().trim().max(200).optional(),
    /**
     * Standart tartib NOM bo'yicha va bu ataylab: faollik bo'yicha
     * tartiblash satrlarni davr o'zgarganda joyidan ko'chiradi — 10 ta
     * kirimli korxona 15 ta kirimli bilan o'rin almashadi va ko'z o'zi
     * qidirgan satrni yo'qotadi. Reyting SO'RALGANDA beriladi.
     */
    sort: flowSortSchema.default('name'),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(FLOW_PAGE_SIZE),
})
    .transform(withRangeDefaults)
    .superRefine(checkReportRange);
export const flowRowSchema = z.object({
    id: z.string(),
    /** Qisqa belgi — «PCH-12», «R va AX». Ichki omborda yo'q. */
    code: z.string().nullable(),
    name: z.string(),
    /**
     * Satr bosilganda qaysi darajaga tushadi. `null` — pastga yo'l yo'q.
     *
     * SERVERDAN keladi, front `level` dan taxmin QILMAYDI: ichki ombor
     * oxirgi daraja, bo'linmasi yo'q korxonada esa strelka chizilmasligi
     * kerak. Taxmin qilinsa, bo'sh jadvalga olib boradigan strelka
     * chizilardi.
     */
    drillLevel: flowLevelSchema.nullable(),
    /** Davr ichida mol kirgan HUJJATLAR soni (harakat satri emas). */
    inDocCount: z.number().int(),
    /** Mol bo'linma hisobidan CHIQQAN hujjatlar soni. */
    outDocCount: z.number().int(),
    /** Hozir turgan partiya soni — davrga bog'liq EMAS. */
    lotCount: z.number().int(),
    /** Nechta quyi tugun bor — «3 bo'linma» deb ko'rsatish uchun. */
    childCount: z.number().int(),
});
/** Nafas chizig'ining bitta bo'g'ini — bosilganda shu darajaga qaytadi. */
export const flowCrumbSchema = z.object({
    level: flowLevelSchema,
    /** `null` — eng yuqori bo'g'in, uning ustida ota yo'q. */
    parentId: z.string().nullable(),
    name: z.string(),
});
export const flowPointSchema = z.object({
    /** `YYYY-MM-DD`. Harakat bo'lmagan kun ham qatorda BO'LADI (nol bilan). */
    date: z.string(),
    inDocCount: z.number().int(),
    outDocCount: z.number().int(),
});
/**
 * Tugunning YIG'MA raqamlari — satrlar yig'indisi EMAS.
 *
 * Jadval ham, detal paneli ham ayni shu shaklni oladi: rahbar satrni
 * bosganda tepadagi raqam bilan panel ichidagi raqam mos kelishi kerak,
 * aks holda u ikki xil haqiqatni ko'radi va ikkisiga ham ishonmaydi.
 */
export const flowTotalsSchema = z.object({
    inDocCount: z.number().int(),
    outDocCount: z.number().int(),
    /** Hozir turgan partiya soni — davrga bog'liq EMAS. */
    lotCount: z.number().int(),
    /**
     * JAVONDAN OLINGAN, lekin hali hisobdan chiqmagan partiya soni.
     *
     * `qty - shelf_qty > 0` — ya'ni «hozir ishlatishda». Miqdorlar
     * YIG'ILMAYDI: bitta tugunda dona, metr va kilogramm aralash turadi
     * va ularning yig'indisi hech narsani anglatmaydi. Partiya soni esa
     * birliksiz va solishtirishga yaroqli.
     */
    inUseLotCount: z.number().int(),
    /** Davr ichida QR bilan javondan olgan xodimlar soni. */
    usageWorkerCount: z.number().int(),
});
export const flowReportSchema = z.object({
    level: flowLevelSchema,
    /**
     * Yo'l — eng yuqoridan joriy darajagacha. Oxirgi bo'g'in HOZIRGI
     * daraja, ya'ni u bosilmaydi.
     */
    breadcrumb: z.array(flowCrumbSchema),
    rows: z.array(flowRowSchema),
    /**
     * Qidiruvga MOS KELGAN satrlar soni — `rows.length` emas.
     *
     * Sahifalash uchun kerak, lekin faqat uning uchun emas: «1000 tadan
     * 1–20» degan yozuv odamga ro'yxat qanchaligini AYTADI va u shu
     * zahoti qidiruvga o'tadi. Usiz birinchi yigirmata satr butun
     * ro'yxatdek ko'rinardi.
     */
    total: z.number().int(),
    /** Butun TUGUNNING raqamlari — ekrandagi sahifaning emas. */
    totals: flowTotalsSchema,
    series: z.array(flowPointSchema),
});
//# sourceMappingURL=flow.schema.js.map